import { X, GitBranch, FolderKanban } from "lucide-react";
import { PROJECT_ICON_MAP } from "@/lib/constants";
import { getProjectColorClasses } from "@/lib/utils/formatting";
import type { Project } from "@/types/dashboard";

interface EnhancedScopeTagProps {
  type: "project" | "branch";
  value: string;
  onRemove: () => void;
  projectData?: Project; // Optional project data for icon/color
}

const EnhancedScopeTag = ({ type, value, onRemove, projectData }: EnhancedScopeTagProps) => {
  const isProject = type === "project";

  const getTagContent = () => {
    // Get icon and colors from project data if available
    const IconComponent = projectData?.icon && PROJECT_ICON_MAP[projectData.icon as keyof typeof PROJECT_ICON_MAP]
      ? PROJECT_ICON_MAP[projectData.icon as keyof typeof PROJECT_ICON_MAP]
      : FolderKanban;
    
    const projectColors = projectData ? getProjectColorClasses(projectData.color) : null;

    if (isProject) {
      return (
        <>
          {projectData ? (
            <div className={`w-4 h-4 rounded ${projectColors?.bg} ${projectColors?.border} border flex items-center justify-center flex-shrink-0`}>
              <IconComponent className={`w-2.5 h-2.5 ${projectColors?.text}`} />
            </div>
          ) : (
            <FolderKanban className="w-3 h-3" />
          )}
          <span>{value}</span>
        </>
      );
    }

    // For branches, split the project/branch format
    const [projectName, branchName] = value.split("/");
    return (
      <>
        {projectData ? (
          <div className={`w-4 h-4 rounded ${projectColors?.bg} ${projectColors?.border} border flex items-center justify-center flex-shrink-0`}>
            <IconComponent className={`w-2.5 h-2.5 ${projectColors?.text}`} />
          </div>
        ) : (
          <FolderKanban className="w-3 h-3" />
        )}
        <span>{projectName}</span>
        <span className="text-muted-foreground">/</span>
        <GitBranch className="w-3 h-3" />
        <span className="font-medium">{branchName}</span>
      </>
    );
  };

  // Use neutral colors that work well with any project color
  const tagColorClasses = "bg-muted text-foreground border-border hover:bg-accent";

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
        border ${tagColorClasses} transition-colors
      `}
    >
      {getTagContent()}
      <button
        onClick={onRemove}
        className="ml-1 -mr-1 p-0.5 rounded-md hover:bg-accent transition-colors"
        aria-label={`Remove ${type} ${value}`}
      >
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
};

export default EnhancedScopeTag;